import { useCallback, useMemo, useState, MouseEvent } from "react";

import Box from "@mui/material/Box";
import MuiDialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import classnames from "classnames";

import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { useTranslate } from "../../../hooks/useTranslate";
import { DashboardButton } from "../Button/DashboardButton";
import { DashboardIconButton } from "../IconButton";
import { DashboardTooltip } from "../Tooltip/DashboardTooltip";
import { useMergeClasses } from "../useMergeClasses";

import { StyledDialog } from "./styles";
import { zeffyDialogClasses, DEFAULT_DIALOG_MAXWIDTH, DEFAULT_DIALOG_VIBE, DialogSsrProps } from "./types";

import { IconWithBackground } from "@/components/IconWithBackground/IconWithBackground";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Error, Success as CheckCircle, Cross as Close, Info, Warning } from "@/icons/outlined";

export const DashboardDialogSsr: React.FC<DialogSsrProps> = ({
  actions,
  children,
  classes: externalClasses,
  icon,
  renderIcon: externalRenderIcon,
  title,
  vibe = DEFAULT_DIALOG_VIBE,
  dialogContentId,
  fullScreenOnMobile,
  hideCloseButton,
  hideIcon = false,
  maxWidth = DEFAULT_DIALOG_MAXWIDTH,
  slots,
  forwardedRef,
  ...rest
}) => {
  const { t } = useTranslate();
  const { isPhoneScreen } = useMediaQuery();
  const isFullScreenMobile = isPhoneScreen && fullScreenOnMobile;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const classes = useMergeClasses(zeffyDialogClasses, externalClasses);

  const vibeIcon = useMemo(() => {
    if (vibe === "brand") {
      return <Info />;
    }
    if (vibe === "danger") {
      return <Error />;
    }
    if (vibe === "warning") {
      return <Warning />;
    }
    if (vibe === "positive") {
      return <CheckCircle />;
    }
  }, [vibe]);

  const renderIcon = useMemo(() => {
    if (hideIcon) {
      return;
    }

    if (externalRenderIcon) {
      return externalRenderIcon();
    }

    return <IconWithBackground vibe={vibe} icon={icon || vibeIcon} />;
  }, [hideIcon, externalRenderIcon, vibe, icon, vibeIcon]);

  const handleClose = useCallback(() => {
    rest.onClose?.();
  }, [rest]);

  const handleSubmit = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      setIsSubmitting(true);
      await actions?.main?.onClick?.(event);
      setIsSubmitting(false);
    },
    [actions?.main]
  );

  const displayCancelButton = actions?.cancel && !actions.cancel.hidden;
  const displayMainButton = actions?.main && !actions.main.hidden;

  const currentDialogClasses = useMemo(
    () => ({
      root: classes.root,
      paper: classes.paper,
      paperFullWidth: classes.paperFullWidth,
      container: classes.container,
    }),
    [classes.container, classes.paper, classes.paperFullWidth, classes.root]
  );

  const mainButton = useMemo(() => {
    if (!actions?.main || !displayMainButton) {
      return null;
    }
    const { tooltip, isLoading, ...buttonProps } = actions.main;
    const button = (
      <DashboardButton
        variant="filled"
        vibe={vibe}
        {...buttonProps}
        onClick={handleSubmit}
        isLoading={isSubmitting || isLoading}
        fullWidth
      >
        {actions?.main.children || t("dashboard", "common.confirm")}
      </DashboardButton>
    );
    if (tooltip) {
      return (
        <DashboardTooltip title={tooltip.title} placement={tooltip.placement}>
          <Grid item>{button}</Grid>
        </DashboardTooltip>
      );
    }
    return <Grid item>{button}</Grid>;
  }, [actions?.main, displayMainButton, handleSubmit, isSubmitting, t, vibe]);

  const cancelButton = useMemo(() => {
    if (!actions?.cancel || !displayCancelButton) {
      return null;
    }
    const { tooltip, ...buttonProps } = actions.cancel;

    const button = (
      <DashboardButton variant="outlined" vibe="neutral" {...buttonProps} fullWidth>
        {actions?.cancel.children || t("dashboard", "common.cancel")}
      </DashboardButton>
    );

    if (tooltip) {
      return (
        <DashboardTooltip title={tooltip.title} placement={tooltip.placement}>
          <Grid item>{button}</Grid>
        </DashboardTooltip>
      );
    }
    return <Grid item>{button}</Grid>;
  }, [actions?.cancel, displayCancelButton, t]);

  const additionalButtons = actions?.additional?.map((buttonProps) => (
    <>
      {!buttonProps.hidden && (
        <Grid item key={buttonProps.children?.toString()}>
          <DashboardButton {...buttonProps} fullWidth />
        </Grid>
      )}
    </>
  ));

  const currentTitle = slots?.title ? (
    slots.title
  ) : title ? (
    <Box>
      <DashboardTypography variant="h6" className={classes.defaultColor}>
        {title}
      </DashboardTypography>
    </Box>
  ) : undefined;

  const hasClose = rest.onClose && !hideCloseButton;

  return (
    <StyledDialog
      {...rest}
      classes={currentDialogClasses}
      ref={forwardedRef}
      fullScreen={isFullScreenMobile || rest.fullScreen}
      maxWidth={maxWidth}
    >
      {(currentTitle || !hideIcon || hasClose) && (
        <div className={classnames(classes.header, classes.globalPadding)}>
          {(!hideIcon || hasClose || (currentTitle && hideIcon)) && (
            <div className={classes.iconCloseContainer}>
              <div className={classes.iconWrapper}>
                {!hideIcon && renderIcon}
                {Boolean(currentTitle && hideIcon) && currentTitle}
              </div>
              {hasClose && (
                <DashboardIconButton vibe="neutral" onClick={handleClose} size="small" data-test="close-dialog">
                  <Close />
                </DashboardIconButton>
              )}
            </div>
          )}

          {currentTitle && !hideIcon && <Box marginTop={!hideIcon || hasClose ? 2 : 0}>{currentTitle}</Box>}
        </div>
      )}

      <MuiDialogContent className={classnames(classes.globalPadding, classes.content)} id={dialogContentId}>
        <DashboardTypography variant="body2">{children}</DashboardTypography>
      </MuiDialogContent>
      {(displayCancelButton || displayMainButton) && (
        <Grid
          container
          className={classnames(classes.footer, classes.globalPadding)}
          justifyContent={isPhoneScreen ? "flex-start" : "flex-end"}
          direction={isPhoneScreen ? "column" : "row"}
          spacing={1}
        >
          {isPhoneScreen ? (
            <>
              {mainButton}
              {cancelButton}
              {additionalButtons}
            </>
          ) : (
            <>
              {additionalButtons}
              {cancelButton}
              {mainButton}
            </>
          )}
        </Grid>
      )}
    </StyledDialog>
  );
};

DashboardDialogSsr.displayName = "DashboardDialog";
